import React, { useEffect, useState, useRef, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faLocationDot, faClock, faUsers, faUser, faWifi, faCalendar, faChevronDown,
  faChevronUp, faChevronRight, faChevronLeft, faCircleCheck, faCircleXmark, faStar,
  faMinus, faPlus, faHotel, faCar, faTimes, faCamera
} from "@fortawesome/free-solid-svg-icons";
import Slider from "react-slick";
import Footer from "../components/Footer";
// import FloatingImage from "../assests/AIicon.png";
import FloatingAIButton from "../components/FloatingAIButton";
import trip_details from "../assests/trip_details.png";
import Modal from "../Modal";
import PolicyModals from "../PolicyModals";
import API_BASE_URL from "../config/api";
import { useFeedback } from "../context/FeedbackContext";
import "./TripDetails.css";



const Arrow = ({ onClick, direction }) => (
  <div style={direction === "next" ? styles.nextArrow : styles.prevArrow} onClick={onClick}>
    <FontAwesomeIcon icon={direction === "next" ? faChevronRight : faChevronLeft} />
  </div>
);

const carouselSettings = {
  infinite: true, speed: 500, slidesToShow: 1, slidesToScroll: 1,
  nextArrow: <Arrow direction="next" />, prevArrow: <Arrow direction="prev" />,
};

const ReadMoreText = ({ text, limit = 150, className = "" }) => {
  const [expanded, setExpanded] = useState(false);

  if (!text) return null;

  // If text is short, just show it
  if (text.length <= limit) {
    return <p className={className}>{text}</p>;
  }

  return (
    <div className={className}>
      <p style={{ margin: 0, display: "inline" }}>
        {expanded ? text : `${text.substring(0, limit)}...`}
      </p>
      <button
        className="read-more-btn"
        onClick={(e) => {
          e.stopPropagation();
          setExpanded(!expanded);
        }}
      >
        {expanded ? "Read Less" : "Read More"}
      </button>
    </div>
  );
};

function TripDetails() {
  const navigate = useNavigate();
  const location = useLocation();
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [packageDetails, setPackageDetails] = useState(null);
  const [error, setError] = useState(null);
  const [isModalOpen, setModalOpen] = useState(false);
  const [selectedDayIndex, setSelectedDayIndex] = useState(0);
  const [isFormModalOpen, setFormModalOpen] = useState(false);
  const [overviewExpanded, setOverviewExpanded] = useState(true);
  const [photoExpanded, setPhotoExpanded] = useState(true);
  const [itineraryExpanded, setItineraryExpanded] = useState(true);
  const [expandedDays, setExpandedDays] = useState({});
  const [currentIndexes, setCurrentIndexes] = useState([]);
  const [hotelDetails, setHotelDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [isBreakdownModalOpen, setIsBreakdownModalOpen] = useState(false);
  const [promoCode, setPromoCode] = useState('');
  const [discountedTotal, setDiscountedTotal] = useState(0);
  const [discountPercentage, setDiscountPercentage] = useState(0);
  const [discountApplied, setDiscountApplied] = useState(false);
  const [tooltipVisible, setTooltipVisible] = useState(false);
  const [promoError, setPromoError] = useState('');
  const [isPromoModalOpen, setIsPromoModalOpen] = useState(false);
  const [promocodes, setPromocodes] = useState([]);
  const [promoFetchError, setPromoFetchError] = useState(null);

  const overviewRef = useRef(null);
  const photosRef = useRef(null);
  const itineraryRef = useRef(null);

  // Feedback hook for booking trigger
  const { showBookingFeedback } = useFeedback();

  // Update windowWidth on resize
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const queryParams = new URLSearchParams(location.search);
  const packageSlug = queryParams.get("name");
  const packageIdFromState = location.state?.selectedPackage || null;

  const [formData, setFormData] = useState({ name: "", email: "", phone: "", selectedPackage: "" });
  const [errors, setErrors] = useState({ email: '', phone: '' });

  const itineraryData = packageDetails?.itinerary?.map((day) => ({
    day: day.day,
    description: day.destinations
      ?.map((d) => d.destinationId?.description || "No description")
      .join(", "),
    points: day.destinations?.map((d) => d.destinationId?.name || "Unnamed"),
    info1: day.hotels?.map((h) => ({
      icon: faHotel,
      text: `${h.hotelId?.name || "Unknown"} (${h.nights || 0} Night${h.nights > 1 ? "s" : ""})`,
    })) || [],
    info2: day.cabs?.map((c) => ({
      icon: faCar,
      text: `${c.cabId?.type || "Car"} (₹${c.cabId?.pricePerDay || 0} Per Day)`,
    })) || [],
    hotels: day.hotels || [],
    cab: day.cabs
      ?.map(
        (c) =>
          `${c.cabId?.type || "Car"} available for ${c.days || 0} day${c.days > 1 ? "s" : ""}. Price: ₹${(c.cabId?.pricePerDay || 0) * (c.days || 0)
          }`
      )
      .join(", "),
    activities: day.destinations?.flatMap((dest) =>
      dest.activities?.map((act) => ({
        name: act.activityId?.name || "Unnamed Activity",
        description: act.activityId?.description || "No description",
      }))
    ) || [],
  })) || [];

  useEffect(() => {
    const fetchPromocodes = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/promocodes`);
        if (!response.ok) {
          throw new Error("Failed to fetch promocodes");
        }
        const result = await response.json();
        // Only show active promocodes to users
        const activePromocodes = result.filter(promo => promo.isActive === true);
        setPromocodes(activePromocodes);
      } catch (error) {
        console.error("Error fetching promocodes:", error);
        setPromoFetchError(error.message);
      }
    };
    fetchPromocodes();
  }, []);

  useEffect(() => {
    if (packageSlug) {
      // Fetch by slug (normal flow from destination pages)
      fetch(`${API_BASE_URL}/tripdetails?name=${packageSlug}`)
        .then((response) => response.json())
        .then((result) => {
          setPackageDetails(result);
          setFormData((prevData) => ({
            ...prevData,
            selectedPackage: result._id,
          }));
          setLoading(false);
        })
        .catch((error) => {
          console.error("Error fetching package details:", error);
          setError(error);
          setLoading(false);
        });
    } else if (packageIdFromState) {
      // Fetch by ID (from My Trips page or direct navigation)
      fetch(`${API_BASE_URL}/packages/${packageIdFromState}`)
        .then((response) => response.json())
        .then((result) => {
          setPackageDetails(result);
          setFormData((prevData) => ({
            ...prevData,
            selectedPackage: result._id,
          }));
          setLoading(false);
        })
        .catch((error) => {
          console.error("Error fetching package by ID:", error);
          setError(error);
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
  }, [packageSlug, packageIdFromState]);

  useEffect(() => {
    if (packageDetails?.itinerary && Object.keys(expandedDays).length === 0) {
      const itineraryLength = packageDetails.itinerary.length;
      setExpandedDays(packageDetails.itinerary.reduce((acc, _, i) => ({ ...acc, [i]: true }), {}));
      setCurrentIndexes(new Array(itineraryLength).fill(0));
    }
  }, [packageDetails, expandedDays]);

  useEffect(() => {
    if (itineraryData?.length > 0 && currentIndexes.length === 0) {
      setCurrentIndexes(new Array(itineraryData.length).fill(0));
    }
  }, [itineraryData, currentIndexes]);

  useEffect(() => {
    if (
      packageDetails?.itinerary?.[selectedDayIndex]?.hotels?.[0]?.hotelId?._id
    ) {
      fetch(`${API_BASE_URL}/hotels/${packageDetails.itinerary[selectedDayIndex].hotels[0].hotelId._id}`)
        .then(response => response.json())
        .then(result => setHotelDetails(result))
        .catch(error => console.error("Error fetching hotel details:", error));
    }
  }, [packageDetails, selectedDayIndex]);

  const applyPromoCode = async (codeToApply = null) => {
    // Use the passed code if it's a valid string, otherwise fall back to promoCode state
    // Handle cases where codeToApply might be an event object or undefined
    const code = (typeof codeToApply === 'string' && codeToApply) ? codeToApply : promoCode;

    // Validate that code is a string and not empty
    if (!code || typeof code !== 'string' || !code.trim()) {
      setPromoError('Please enter a promo code');
      return;
    }

    const { overall } = computeBreakdown();
    const currentTotal = overall.finalTotal;
    const basePrice = overall.base;

    console.log('Applying Promo Code:', {
      code,
      originalTotal: currentTotal
    });

    try {
      const response = await fetch(`${API_BASE_URL}/promocodes/validate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          code: code,  // Use the code variable instead of promoCode state
          totalAmount: currentTotal,  // Use final total for minimum purchase check
        }),
      });
      const result = await response.json();
      console.log('Promo Code Validation Result:', result);
      if (response.ok) {
        if (!result.discount) {
          console.error('Invalid backend response', result);
          setPromoError('Invalid backend response');
          return;
        }

        // NEW Calculation: Cost Price + [(Service Charge - Discount) + GST]
        // Where: Service Charge = 10% of Cost Price
        //        Discount = applied to Service Charge (discount percentage on service charge)
        //        GST = 18% of (Service Charge - Discount)

        const serviceCharge = basePrice * 0.1;  // 10% service charge
        const discountAmt = serviceCharge * (result.discount / 100);  // Discount on service charge
        const netServiceCharge = serviceCharge - discountAmt;  // Service charge after discount
        const gst = netServiceCharge * 0.18;  // 18% GST on net service charge
        const newFinalTotal = basePrice + netServiceCharge + gst;  // Net Payable Amount

        setDiscountedTotal(newFinalTotal);
        setDiscountPercentage(result.discount);
        setDiscountApplied(true);
        console.log('Discount Applied:', {
          originalTotal: currentTotal,
          discountedTotal: newFinalTotal,
          discountPercentage: result.discount
        });
      } else {
        setDiscountApplied(false);
        setDiscountedTotal(currentTotal);
        setDiscountPercentage(0);
        setPromoError(result.message || 'Invalid promocode');
      }
    } catch (error) {
      console.error('Error validating promocode:', error);
      setDiscountApplied(false);
      setDiscountedTotal(currentTotal);
      setDiscountPercentage(0);
      setPromoError('Error validating promocode');
    }
  };

  // Compute responsive styles based on windowWidth
  const computedStyles = {
    ...styles,
    container: {
      ...styles.container,
      flexDirection: windowWidth < 768 ? "column" : "row",
      padding: windowWidth < 480 ? "10px" : windowWidth < 768 ? "15px" : "20px",
    },
    destinationPage: {
      ...styles.destinationPage,
      padding: windowWidth < 480 ? "10px" : windowWidth < 768 ? "15px" : "20px",
    },
    destinationHeader: {
      ...styles.destinationHeader,
      marginBottom: windowWidth < 768 ? "15px" : "20px",
    },
    leftContent: {
      ...styles.leftContent,
      left: windowWidth < 768 ? "20px" : "100px",
    },
    h1: {
      ...styles.h1,
      fontSize: windowWidth < 480 ? "40px" : windowWidth < 768 ? "50px" : "70px",
      marginLeft: windowWidth < 768 ? "-20px" : "-40px",
    },
    rightImage: {
      ...styles.rightImage,
      width: "100%",
      height: windowWidth < 768 ? "200px" : "300px",
    },
    cityPhotoContainer: {
      ...styles.cityPhotoContainer,
      width: "100%",
    },
    cityPhoto: {
      ...styles.cityPhoto,
      width: "100%",
      height: windowWidth < 480 ? "200px" : windowWidth < 768 ? "300px" : "450px",
    },
    buttonsContainer: {
      ...styles.buttonsContainer,
      bottom: windowWidth < 768 ? "20px" : "30px",
      left: windowWidth < 768 ? "20px" : "40px",
    },
    smallButton: {
      ...styles.smallButton,
      padding: windowWidth < 480 ? "8px 16px" : "10px 20px",
      fontSize: windowWidth < 480 ? "13px" : "15px",
    },
    sidebar: {
      ...styles.sidebar,
      width: windowWidth < 768 ? "100%" : "25%",
      padding: windowWidth < 480 ? "5px" : "10px",
      marginTop: windowWidth < 768 ? "20px" : "0",
    },
    mainContent: {
      ...styles.mainContent,
      width: windowWidth < 768 ? "100%" : "75%",
      padding: windowWidth < 480 ? "0 10px" : "0 20px",
    },
    tabs: {
      ...styles.tabs,
      flexDirection: windowWidth < 480 ? "column" : "row",
    },
    tabButton: {
      ...styles.tabButton,
      fontSize: windowWidth < 480 ? "14px" : windowWidth < 768 ? "15px" : "17px",
      padding: windowWidth < 480 ? "8px 10px" : "10px 20px",
    },
    section: {
      ...styles.section,
      marginTop: windowWidth < 768 ? "20px" : "30px",
    },
    mainHeading: {
      ...styles.mainHeading,
      fontSize: windowWidth < 480 ? "25px" : windowWidth < 768 ? "30px" : "35px",
    },
    infoRow: {
      ...styles.infoRow,
      flexDirection: windowWidth < 480 ? "column" : "row",
      gap: windowWidth < 480 ? "10px" : "0",
    },
    infoItem: {
      ...styles.infoItem,
      fontSize: windowWidth < 480 ? "14px" : windowWidth < 768 ? "15px" : "17px",
    },
    sectionHeading: {
      ...styles.sectionHeading,
      fontSize: windowWidth < 480 ? "20px" : windowWidth < 768 ? "22px" : "25px",
    },
    sectionText: {
      ...styles.sectionText,
      fontSize: windowWidth < 480 ? "14px" : windowWidth < 768 ? "15px" : "17px",
    },
    carouselContainer: {
      ...styles.carouselContainer,
      width: "100%",
    },
    carouselImage: {
      ...styles.carouselImage,
      height: windowWidth < 480 ? "200px" : windowWidth < 768 ? "300px" : "391px",
    },
    dayHeading: {
      ...styles.dayHeading,
      fontSize: windowWidth < 480 ? "14px" : windowWidth < 768 ? "16px" : "18px",
    },
    hotelImage: {
      ...styles.hotelImage,
      width: windowWidth < 480 ? "150px" : windowWidth < 768 ? "180px" : "201px",
      height: windowWidth < 480 ? "100px" : windowWidth < 768 ? "120px" : "133px",
    },
    changeButton: {
      ...styles.changeButton,
      padding: windowWidth < 480 ? "5px 20px" : "5px 30px",
      fontSize: windowWidth < 480 ? "10px" : "12px",
      marginLeft: windowWidth < 768 ? "20px" : "50px",
    },
    bookButton: {
      ...styles.bookButton,
      padding: windowWidth < 480 ? "8px 20px" : "10px 30px",
      fontSize: windowWidth < 480 ? "18px" : "22px",
    },
    priceText: {
      ...styles.priceText,
      fontSize: windowWidth < 480 ? "22px" : windowWidth < 768 ? "24px" : "28px",
    },
    contactCard: {
      ...styles.contactCard,
      width: "100%",
      maxWidth: windowWidth < 768 ? "100%" : "370px",
    },
    cardHeading: {
      ...styles.cardHeading,
      fontSize: windowWidth < 480 ? "20px" : windowWidth < 768 ? "22px" : "25px",
    },
    cardText: {
      ...styles.cardText,
      fontSize: windowWidth < 480 ? "14px" : "16px",
    },
    modalOverlay: {
      ...styles.modalOverlay,
      padding: windowWidth < 480 ? "10px" : "20px",
    },
    modalContent: {
      ...styles.modalContent,
      maxWidth: windowWidth < 480 ? "90%" : windowWidth < 768 ? "500px" : "600px",
      padding: windowWidth < 480 ? "15px" : "20px",
    },
    modalTitle: {
      ...styles.modalTitle,
      fontSize: windowWidth < 480 ? "20px" : "24px",
    },
    inputLabel: {
      ...styles.inputLabel,
      fontSize: windowWidth < 480 ? "14px" : "16px",
    },
    inputField: {
      ...styles.inputField,
      padding: windowWidth < 480 ? "8px" : "10px",
    },
    submitButton: {
      ...styles.submitButton,
      padding: windowWidth < 480 ? "8px 16px" : "10px 20px",
      fontSize: windowWidth < 480 ? "14px" : "16px",
    },
    closeButton: {
      ...styles.closeButton,
      padding: windowWidth < 480 ? "8px 16px" : "10px 20px",
      fontSize: windowWidth < 480 ? "14px" : "16px",
    },
    promoToggleButton: {
      ...styles.promoToggleButton,
      padding: windowWidth < 480 ? "8px 16px" : "10px 20px",
      fontSize: windowWidth < 480 ? "14px" : "16px",
      maxWidth: windowWidth < 768 ? "100%" : "300px",
    },
    promoCard: {
      ...styles.promoCard,
      padding: windowWidth < 480 ? "10px" : "15px",
    },
    promoCardTitle: {
      ...styles.promoCardTitle,
      fontSize: windowWidth < 480 ? "16px" : "18px",
    },
    promoCardText: {
      ...styles.promoCardText,
      fontSize: windowWidth < 480 ? "12px" : "14px",
    },
    promoApplyButton: {
      ...styles.promoApplyButton,
      padding: windowWidth < 480 ? "6px 12px" : "8px 15px",
      fontSize: windowWidth < 480 ? "12px" : "14px",
    },
    floatingBox: {
      ...styles.floatingBox,
      width: windowWidth < 480 ? "80px" : "96px",
      height: windowWidth < 480 ? "80px" : "96px",
    },
    floatingImage: {
      ...styles.floatingImage,
      width: windowWidth < 480 ? "80px" : "96px",
      height: windowWidth < 480 ? "80px" : "96px",
    },
    spinnerContainer: {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      height: "100vh",
      flexDirection: "column",
      gap: "10px",
    },
    spinner: {
      width: "50px",
      height: "50px",
      border: "6px solid #ddd",
      borderTop: "6px solid #02495B",
      borderRadius: "50%",
      animation: "spin 1s linear infinite",
    },
  };

  if (loading) {
    return (
      <div style={computedStyles.spinnerContainer}>
        <div style={computedStyles.spinner}></div>
        <p>Loading package details...</p>
        <style>
          {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
        </style>
      </div>
    );
  }

  if (error) return <div>Error loading package details.</div>;

  const formOpenModal = () => {
    setFormModalOpen(true);
  };

  const formCloseModal = () => {
    setFormModalOpen(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    if ((name === 'email' || name === 'phone') && !value.trim()) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        [name]: `${name.charAt(0).toUpperCase() + name.slice(1)} is required`
      }));
    }
  };

  const validateForm = () => {
    let newErrors = {};
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    }
    if (!formData.phone.trim()) {
      newErrors.phone = "Phone number is required";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validateForm()) {
      return;
    }
    setLoadingSubmit(true);
    const { overall } = computeBreakdown();
    const originalTotal = overall.finalTotal;

    const payload = {
      ...formData,
      totalPrice: originalTotal,
      finalPrice: discountedTotal,
      discountApplied: discountApplied,
      discountPercentage: discountPercentage
    };

    const myHeaders = new Headers();
    myHeaders.append("Content-Type", "application/json");
    // Idempotency key to prevent duplicate bookings on retry
    myHeaders.append("X-Idempotency-Key", `booking-${formData.email}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`);

    const requestOptions = {
      method: "POST",
      headers: myHeaders,
      body: JSON.stringify(payload),
      redirect: "follow",
    };

    fetch(`${API_BASE_URL}/send-package-details`, requestOptions)
      .then((response) => response.json())
      .then((result) => {
        console.log(result);
        // Save userId to localStorage for My Trips feature
        if (result.userId) {
          localStorage.setItem('trippro_userId', result.userId);
        }
        if (formData.email) {
          localStorage.setItem('trippro_userEmail', formData.email.toLowerCase().trim());
        }
        alert("Booking confirmed! Redirecting to your trips...");
        setFormModalOpen(false);
        navigate('/my-trips');
      })
      .catch((error) => {
        console.error(error);
        alert("Failed to submit details.");
      })
      .finally(() => {
        setLoadingSubmit(false);
      });
  };

  const extractDestinationsAndFormatItinerary = () => {
    const itinerary = packageDetails?.itinerary || [];
    const destinations = itinerary.flatMap((day) =>
      day.destinations.map((dest) => ({
        ...dest.destinationId,
        activities: dest.activities || [],
        destinationEntryId: dest._id,
      }))
    );

    if (!destinations.length) {
      console.error("No destinations found in the package details.");
      alert("Destination information is missing. Please check the package details.");
      return null;
    }

    const formattedItinerary = itinerary.map((day) => ({
      day: day.day,
      destinations: day.destinations.map((dest) => ({
        destinationId: { ...dest.destinationId },
        activities: dest.activities || [],
        _id: dest._id,
      })),
      hotels: day.hotels.map((hotel) => ({
        hotelId: { ...hotel.hotelId },
        nights: hotel.nights,
        roomType: hotel.roomType,
        _id: hotel._id,
      })),
      cabs: day.cabs.map((cab) => ({
        cabId: { ...cab.cabId },
        days: cab.days,
        _id: cab._id,
      })),
      _id: day._id,
    }));

    return { destinations, formattedItinerary };
  };

  const handleViewHotelClick = (dayIndex) => {
    setSelectedDayIndex(dayIndex);
    const data = extractDestinationsAndFormatItinerary();
    if (!data) return;

    const itineraryDetails = {
      _id: packageDetails._id,
      name: packageDetails.name,
      description: packageDetails.description,
      coverImage: packageDetails.coverImage,
      totalPrice: packageDetails.totalPrice,
      startDate: packageDetails.startDate,
      endDate: packageDetails.endDate,
      notes: packageDetails.notes,
      itinerary: data.formattedItinerary,
      destinations: data.destinations,
      __v: packageDetails.__v,
    };

    console.log("Navigating to hotel page with:", { itineraryDetails });
    navigate("/hotel", { state: { itineraryDetails, dayIndex } });
  };

  const handleViewCabClick = (dayIndex) => {
    const data = extractDestinationsAndFormatItinerary();
    if (!data) return;

    const itineraryDetails = {
      _id: packageDetails._id,
      name: packageDetails.name,
      description: packageDetails.description,
      coverImage: packageDetails.coverImage,
      totalPrice: packageDetails.totalPrice,
      startDate: packageDetails.startDate,
      endDate: packageDetails.endDate,
      notes: packageDetails.notes,
      itinerary: data.formattedItinerary,
      destinations: data.destinations,
      __v: packageDetails.__v,
    };
    navigate("/cab", {
      state: {
        itineraryDetails,
        packageId: packageDetails._id,
        dayIndex: dayIndex
      }
    });
  };

  const handleViewActivitiesClick = (dayIndex) => {
    const data = extractDestinationsAndFormatItinerary();
    if (!data) return;

    const destinationId = packageDetails?.itinerary?.[dayIndex]?.destinations?.[0]?.destinationId?._id;

    if (!destinationId) {
      alert("Destination information is missing. Please check the package details.");
      return;
    }

    const itineraryDetails = {
      _id: packageDetails._id,
      name: packageDetails.name,
      description: packageDetails.description,
      coverImage: packageDetails.coverImage,
      totalPrice: packageDetails.totalPrice,
      startDate: packageDetails.startDate,
      endDate: packageDetails.endDate,
      notes: packageDetails.notes,
      itinerary: data.formattedItinerary,
      destinations: data.destinations,
      __v: packageDetails.__v,
    };

    console.log("Navigating to activities page with:", { itineraryDetails, destinationId });
    navigate("/activities", { state: { itineraryDetails, destinationId, dayIndex } });
  };

  const openModal = () => {
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
  };

  const handleScrollToSection = (ref) => {
    ref.current.scrollIntoView({ behavior: "smooth" });
  };

  const handleToggle = () => {
    setOverviewExpanded(!overviewExpanded);
  };

  const handleTogglePhoto = () => {
    setPhotoExpanded(!photoExpanded);
  };

  const handleToggleItinerary = () => {
    setItineraryExpanded(!itineraryExpanded);
  };

  const handleToggleDay = (dayIdx) => {
    setExpandedDays((prevState) => ({
      ...prevState,
      [dayIdx]: !prevState[dayIdx],
    }));
  };

  const handleSwipe = (dayIndex, direction) => {
    setCurrentIndexes((prevIndexes) => {
      const imagesArray = itineraryData?.[dayIndex]?.hotels?.[0]?.hotelId?.seasonalRates?.[0]?.rates?.[0]?.images;

      if (!imagesArray || imagesArray.length === 0) {
        console.warn(`No images available for day index ${dayIndex}`);
        return prevIndexes;
      }

      const newIndexes = [...prevIndexes];
      const totalImages = imagesArray.length;
      const visibleImagesCount = 3;
      const totalPages = Math.ceil(totalImages / visibleImagesCount);

      if (direction === "left") {
        newIndexes[dayIndex] = (newIndexes[dayIndex] - 1 + totalPages) % totalPages;
      } else if (direction === "right") {
        newIndexes[dayIndex] = (newIndexes[dayIndex] + 1) % totalPages;
      }

      return newIndexes;
    });
  };

  const openImageModal = (image) => {
    setSelectedImage(image);
  };

  const closeImageModal = () => {
    setSelectedImage(null);
  };

  const togglePromoModal = () => {
    setIsPromoModalOpen(!isPromoModalOpen);
  };

  const handleApplyPromoFromCard = (code) => {
    setPromoCode(code);
    setPromoError("");
    setDiscountApplied(false);
    setDiscountPercentage(0);
    setDiscountedTotal(computeBreakdown().overall.finalTotal);
    // Pass the code directly to avoid state timing issues
    applyPromoCode(code);
  };

  const computeBreakdown = () => {
    if (!packageDetails) {
      return {
        dayBreakdowns: [],
        overall: { hotelsTotal: 0, cabsTotal: 0, activitiesTotal: 0, base: 0, serviceCharge: 0, gst: 0, finalTotal: 0 }
      };
    }

    let overallHotelsTotal = 0, overallCabsTotal = 0, overallActivitiesTotal = 0;
    const dayBreakdowns = packageDetails.itinerary?.map(day => {
      const hotelsTotal = (day.hotels || []).reduce((sum, hotel) => {
        let rate = 0;
        if (hotel.hotelId?.seasonalRates && hotel.hotelId.seasonalRates.length > 0) {
          const season = hotel.hotelId.seasonalRates[0];
          if (season.rates && season.rates.length > 0) {
            const matchingRate = season.rates.find(r => r.roomType === hotel.roomType);
            rate = matchingRate ? matchingRate.specialRateCPAI : season.rates[0].specialRateCPAI;
          }
        }
        const nights = hotel.nights || 0;
        return sum + (rate * nights);
      }, 0);

      const cabsTotal = (day.cabs || []).reduce((sum, cab) =>
        sum + (cab.days * (cab.cabId?.pricePerDay || 0)), 0);

      const activitiesTotal = (day.destinations || []).reduce((destSum, dest) =>
        destSum + (dest.activities || []).reduce((actSum, activity) =>
          actSum + (activity.activityId?.price || 0), 0), 0);

      const dayBase = hotelsTotal + cabsTotal + activitiesTotal;
      // Day total is just the base - service/GST applied only at overall level
      const dayTotal = dayBase;

      overallHotelsTotal += hotelsTotal;
      overallCabsTotal += cabsTotal;
      overallActivitiesTotal += activitiesTotal;

      return {
        day: day.day,
        hotelsTotal,
        cabsTotal,
        activitiesTotal,
        dayTotal
      };
    }) || [];

    // Calculate overall totals using NEW formula
    const overallBase = overallHotelsTotal + overallCabsTotal + overallActivitiesTotal;
    const overallServiceCharge = overallBase * 0.10; // 10% service charge on base
    const overallGST = overallServiceCharge * 0.18; // 18% GST on service charge ONLY (NEW formula)
    const netPayable = overallBase + overallServiceCharge + overallGST;

    return {
      dayBreakdowns,
      overall: {
        hotelsTotal: overallHotelsTotal,
        cabsTotal: overallCabsTotal,
        activitiesTotal: overallActivitiesTotal,
        base: overallBase,
        serviceCharge: overallServiceCharge,
        gst: overallGST,
        finalTotal: netPayable
      }
    };
  };

  const PriceDisplay = ({
    packageDetails,
    computeBreakdown,
    promoCode,
    setPromoCode,
    discountedTotal,
    setDiscountedTotal,
    discountPercentage,
    setDiscountPercentage,
    discountApplied,
    setDiscountApplied,
    promoError,
    setPromoError,
    applyPromoCode
  }) => {
    const originalTotal = useMemo(() => {
      const { overall } = computeBreakdown();
      return overall.finalTotal;
    }, [computeBreakdown]);

    useEffect(() => {
      console.log('Initial Price Display State:', {
        originalTotal,
        promoCode,
        discountedTotal,
        discountPercentage,
        discountApplied
      });
    }, [originalTotal, promoCode, discountedTotal, discountPercentage, discountApplied]);

    useEffect(() => {
      if (!discountApplied) {
        setDiscountedTotal(originalTotal);
      }
    }, [originalTotal, discountApplied, setDiscountedTotal]);

    const handlePromoCodeChange = (e) => {
      const newPromoCode = e.target.value;
      console.log('Promo Code Changed:', newPromoCode);

      setPromoCode(newPromoCode);
      setPromoError('');
      setDiscountApplied(false);
      setDiscountPercentage(0);
      setDiscountedTotal(originalTotal);
    };

    if (!packageDetails) return null;

    return (
      <div>
        <p style={computedStyles.priceText}>
          {discountApplied ? (
            <>
              <span
                style={{ textDecoration: 'line-through', color: '#888', marginRight: '10px' }}
              >
                Rs {originalTotal.toFixed(2)}/-
              </span>
              Rs {discountedTotal.toFixed(2)}/-
            </>
          ) : (
            `Rs ${originalTotal.toFixed(2)}/-`
          )}{' '}
          Total
          <span
            style={{ marginLeft: '8px', position: 'relative', display: 'inline-block' }}
            onMouseEnter={() => setTooltipVisible(true)}
            onMouseLeave={() => setTooltipVisible(false)}
          >
            <span
              style={{
                display: 'inline-block', width: '16px', height: '16px', borderRadius: '50%',
                backgroundColor: '#ccc', color: '#fff', textAlign: 'center', lineHeight: '16px',
                fontSize: '12px', cursor: 'pointer'
              }}
            >
              i
            </span>
            {tooltipVisible && (
              <span
                style={{
                  position: 'absolute', fontSize: 12, bottom: '100%', left: windowWidth < 768 ? '-200%' : '-500%',
                  transform: 'translateX(-50%)', backgroundColor: 'black', color: '#fff',
                  padding: '5px 10px', borderRadius: '6px', whiteSpace: 'nowrap', zIndex: 1
                }}
              >
                To see the price breakup click on Book Now
              </span>
            )}
          </span>
        </p>
        <div
          style={{ display: 'flex', alignItems: 'center', marginTop: '20px', marginBottom: '20px', flexDirection: windowWidth < 480 ? 'column' : 'row', gap: windowWidth < 480 ? '10px' : '0' }}
        >
          <input
            type="text"
            placeholder="Enter promo code"
            value={promoCode}
            onChange={handlePromoCodeChange}
            style={{ padding: windowWidth < 480 ? '6px' : '8px', marginRight: windowWidth < 480 ? '0' : '10px', border: '1px solid #ccc', borderRadius: '4px', flex: 1, width: windowWidth < 480 ? '100%' : 'auto' }}
          />
          <button
            onClick={applyPromoCode}
            style={{ padding: windowWidth < 480 ? '6px 12px' : '8px 15px', backgroundColor: '#28A745', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', width: windowWidth < 480 ? '100%' : 'auto' }}
          >
            Apply
          </button>
        </div>
        {promoError && (
          <p style={{ color: "red", marginTop: "5px", fontSize: windowWidth < 480 ? "12px" : "14px" }}>
            {promoError}
          </p>
        )}
        {discountApplied && (
          <p style={{ color: "green", marginTop: "5px", fontSize: windowWidth < 480 ? "12px" : "14px" }}>
            {discountPercentage}% discount applied!
          </p>
        )}
      </div>
    );
  };

  return (
    <>
      <Navbar />
      <div style={computedStyles.destinationPage} className="main-container">

        {/* Modern Unified Hero Section */}
        <div style={computedStyles.cityPhotoContainer} className="city-photo-container">
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: 'linear-gradient(180deg, rgba(2, 73, 91, 0.3) 0%, rgba(2, 73, 91, 0.1) 40%, rgba(0, 0, 0, 0.6) 100%)',
            zIndex: 1
          }} />

          <img
            style={computedStyles.cityPhoto}
            className="city-photo"
            src={
              packageDetails?.coverImage ||
              "https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=600&auto=format&fit=crop&q=60&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxzZWFyY2h8MTF8fGNvdmVyJTIwaW1hZ2V8ZW58MHx8MHx8fDA%3D"
            }
            alt="City"
          />

          <div style={computedStyles.leftContent} className="left-content">
            <h1 style={{ ...computedStyles.h1, color: 'white', textShadow: '0 4px 12px rgba(0,0,0,0.3)', marginTop: 0 }} className="destination-title">
              {packageDetails?.itinerary?.[0]?.destinations?.[0]?.destinationId?.name || "Destination"}
            </h1>
          </div>

          <div style={computedStyles.buttonsContainer} className="buttons-container">
            <button className="small-button" style={{ ...computedStyles.smallButton, background: 'rgba(255, 255, 255, 0.2)', backdropFilter: 'blur(10px)', border: '1px solid rgba(255,255,255,0.3)' }}>
              <FontAwesomeIcon icon={faCamera} style={{ marginRight: '8px' }} />
              View Photos
            </button>
          </div>
        </div>

        <div style={{
          ...computedStyles.container,
          padding: windowWidth < 768 ? "1rem" : "2rem 2.5rem",
        }}>
          {/* Sidebar appears first on mobile for better UX */}
          <aside style={{
            ...computedStyles.sidebar,
            order: windowWidth < 768 ? -1 : 1,
            marginBottom: windowWidth < 768 ? "2rem" : 0,
          }}>
            <div style={{
              position: windowWidth < 768 ? "relative" : "sticky",
              top: windowWidth < 768 ? 0 : "20px",
            }}>
              <div style={computedStyles.buttonContainer}>
                <button style={computedStyles.bookButton} onClick={formOpenModal}>Book Now</button>
              </div>
              <PriceDisplay
                packageDetails={packageDetails}
                computeBreakdown={computeBreakdown}
                promoCode={promoCode}
                setPromoCode={setPromoCode}
                discountedTotal={discountedTotal}
                setDiscountedTotal={setDiscountedTotal}
                discountPercentage={discountPercentage}
                setDiscountPercentage={setDiscountPercentage}
                discountApplied={discountApplied}
                setDiscountApplied={setDiscountApplied}
                promoError={promoError}
                setPromoError={setPromoError}
                applyPromoCode={applyPromoCode}
              />
              <div style={computedStyles.contactCard}>
                <div style={computedStyles.overlay} />
                <div style={computedStyles.contactContent}>
                  <h3 style={computedStyles.cardHeading}>Have a question?</h3>
                  <p style={computedStyles.cardText}>
                    We're here to help! Whether you're curious about our services, need assistance, or just want to say hello, feel free to reach out.
                  </p>
                  <div style={computedStyles.contactInfo}>
                    <div style={computedStyles.contactDetail}>
                      <FontAwesomeIcon icon={faUser} />
                      +91 97112 78815
                    </div>
                    <div style={computedStyles.contactDetail}>
                      <FontAwesomeIcon icon={faWifi} />
                      contact@trippro.in
                    </div>
                  </div>
                </div>
              </div>
              {/* Promo Codes Section */}
              <div style={{ marginTop: "1.5rem" }}>
                <button style={computedStyles.promoToggleButton} onClick={togglePromoModal}>
                  Show Available Promocodes
                </button>
                {isPromoModalOpen && (
                  <div style={{
                    marginTop: "1rem",
                    padding: "1rem",
                    backgroundColor: "#f8f9fa",
                    borderRadius: "12px",
                    maxHeight: "300px",
                    overflowY: "auto"
                  }}>
                    {promoFetchError ? (
                      <p style={{ color: "red", textAlign: "center" }}>{promoFetchError}</p>
                    ) : promocodes.length === 0 ? (
                      <p style={{ textAlign: "center", color: "#666" }}>No promocodes available at the moment.</p>
                    ) : (
                      promocodes.map((promo, index) => (
                        <div key={index} style={computedStyles.promoCard}>
                          <h4 style={computedStyles.promoCardTitle}>{promo.code}</h4>
                          <p style={computedStyles.promoCardText}>{promo.discount}% off</p>
                          <p style={computedStyles.promoCardText}>{promo.description || "Apply this code at checkout"}</p>
                          <button
                            style={computedStyles.promoApplyButton}
                            onClick={() => handleApplyPromoFromCard(promo.code)}
                            disabled={discountApplied && promoCode === promo.code}
                          >
                            {discountApplied && promoCode === promo.code ? "Applied" : "Apply"}
                          </button>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            </div>
          </aside>

          <main style={{
            ...computedStyles.mainContent,
            order: windowWidth < 768 ? 1 : 0,
          }}>
            <div style={computedStyles.tabs}>
              {['Overview', 'Photos', 'Itinerary'].map((label, index) => (
                <button
                  key={index}
                  style={computedStyles.tabButton}
                  onClick={() => handleScrollToSection([overviewRef, photosRef, itineraryRef][index])}
                >
                  {label}
                </button>
              ))}
            </div>
            <section ref={overviewRef} style={computedStyles.section}>
              <h1 style={computedStyles.mainHeading}>{packageDetails.name}</h1>
              <div style={computedStyles.infoRow}>
                {[
                  { icon: faClock, text: `${Math.ceil((new Date(packageDetails.endDate) - new Date(packageDetails.startDate)) / (1000 * 3600 * 24))} Days` },
                  { icon: faUsers, text: "Available for group as well" }
                ].map(({ icon, text }, index) => (
                  <div key={index} style={computedStyles.infoItem}>
                    <FontAwesomeIcon icon={icon} style={{ color: "#1CA8CB", ...computedStyles.icon }} /> {text}
                  </div>
                ))}
              </div>
              <div style={computedStyles.infoRow}>
                {[
                  { icon: faCalendar, text: `${new Date(packageDetails.startDate).toLocaleDateString("en-US", { month: "short", day: "numeric" })} - ${new Date(packageDetails.endDate).toLocaleDateString("en-US", { month: "short", day: "numeric" })}` },
                  { icon: faLocationDot, text: packageDetails.itinerary.flatMap(day => day.destinations.map(dest => dest.destinationId.name)).join(", ") }
                ].map(({ icon, text }, index) => (
                  <div key={index} style={computedStyles.infoItem}>
                    <FontAwesomeIcon icon={icon} style={{ color: "#1CA8CB", ...computedStyles.icon }} /> {text}
                  </div>
                ))}
              </div>
              <hr style={computedStyles.divider} />
              <h2 style={{ ...computedStyles.sectionHeading, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                Overview
                <FontAwesomeIcon
                  icon={overviewExpanded ? faChevronUp : faChevronDown}
                  style={{ cursor: "pointer" }}
                  onClick={handleToggle}
                />
              </h2>
              {overviewExpanded && (
                <p style={computedStyles.sectionText}>{packageDetails.description}</p>
              )}
            </section>
            <hr style={computedStyles.divider} />
            <div style={computedStyles.section} ref={photosRef}>
              <h2 style={{ ...computedStyles.sectionHeading, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                Photos
                <FontAwesomeIcon icon={photoExpanded ? faChevronUp : faChevronDown} style={{ cursor: "pointer" }} onClick={handleTogglePhoto} />
              </h2>
              {photoExpanded && (
                <div style={computedStyles.carouselContainer}>
                  <Slider {...carouselSettings}>
                    {packageDetails.itinerary.map((day, dayIndex) =>
                      day.destinations.map((destination, destinationIndex) =>
                        destination.destinationId.images.map((image, imageIndex) => (
                          <div key={`${dayIndex}-${destinationIndex}-${imageIndex}`}>
                            <img
                              src={image}
                              alt={`Destination ${destinationIndex + 1} - Image ${imageIndex + 1}`}
                              style={computedStyles.carouselImage}
                            />
                          </div>
                        ))
                      )
                    )}
                  </Slider>
                </div>
              )}
            </div>
            <hr style={computedStyles.divider} />
            <div ref={itineraryRef} style={computedStyles.section}>
              <h2 style={{ ...computedStyles.sectionHeading, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                Itinerary
                <FontAwesomeIcon icon={itineraryExpanded ? faChevronUp : faChevronDown} style={{ cursor: "pointer" }} onClick={handleToggleItinerary} />
              </h2>
              {itineraryExpanded && (
                <div className="itinerary-content">
                  <div className="timeline-wrapper">
                    <div className="vertical-line"></div>
                    {itineraryData.map((dayData, dayIdx) => (
                      <div key={dayIdx} className="day-container">
                        {/* Day Heading Card */}
                        <div
                          className={`day-heading ${expandedDays[dayIdx] ? 'active' : ''}`}
                          onClick={() => handleToggleDay(dayIdx)}
                        >
                          <div className="day-title">
                            <div className="day-icon-wrapper">
                              <FontAwesomeIcon icon={faLocationDot} className="day-icon" />
                            </div>
                            <span className="day-label">
                              <span className="day-number">Day {dayData.day}:</span>
                              <span className="day-desc-short">{dayData.description}</span>
                            </span>
                          </div>
                          <div className="toggle-icon-wrapper">
                            <FontAwesomeIcon
                              icon={expandedDays[dayIdx] ? faMinus : faPlus}
                              className="toggle-day-icon"
                            />
                          </div>
                        </div>

                        {/* Expandable Content */}
                        <div className={`day-content ${expandedDays[dayIdx] ? 'expanded' : ''}`}>
                          {expandedDays[dayIdx] && (
                            <>
                              <div className="day-body">
                                {/* Points List */}
                                <ul className="itinerary-list">
                                  {dayData.points.map((point, idx) => (
                                    <li key={idx} className="itinerary-item">
                                      <ReadMoreText text={point} limit={200} className="common-text" />
                                    </li>
                                  ))}
                                </ul>

                                {/* Info Rows (Hotels, etc.) */}
                                <div className="info-row-grid">
                                  {dayData.info1?.map((infoItem, idx) => infoItem?.icon && (
                                    <div key={idx} className="info-card">
                                      <FontAwesomeIcon icon={infoItem.icon} className="info-icon" />
                                      <p className="info-text">
                                        {infoItem.text || "N/A"}
                                      </p>
                                    </div>
                                  ))}
                                  {(dayData.hotels || []).map((hotel, idx) => {
                                    const currentSeason = hotel.hotelId?.seasonalRates.find(rate => {
                                      const now = new Date();
                                      return now >= new Date(rate.startDate) && now <= new Date(rate.endDate);
                                    });
                                    const roomType = hotel.roomType || currentSeason?.rates?.[0]?.roomType || "Room Type Unavailable";
                                    return (
                                      <div key={idx} className="info-card">
                                        <div className="hotel-info">
                                          <p className="info-text"><strong>Room:</strong> {roomType}</p>
                                          <p className="info-text"><strong>Nights:</strong> {hotel.nights || 0}</p>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>

                                {/* Info Rows 2 (Cabs) */}
                                <div className="info-row-grid">
                                  {dayData.info2.map((infoItem, idx) => (
                                    <div key={idx} className="info-card">
                                      <FontAwesomeIcon icon={infoItem.icon} className="info-icon" />
                                      <span className="info-text">{infoItem.text}</span>
                                    </div>
                                  ))}
                                </div>

                                {/* Hotel Section */}
                                <div className="sub-section">
                                  <div className="sub-section-header">
                                    <h3 className="sub-section-title">Hotel</h3>
                                    <button className="change-btn" onClick={() => handleViewHotelClick(dayIdx)}>Change</button>
                                  </div>
                                  <div className="hotel-gallery">
                                    <div className="swipe-controls">
                                      <button className="nav-btn prev" onClick={() => handleSwipe(dayIdx, "left")}>
                                        <FontAwesomeIcon icon={faChevronLeft} />
                                      </button>
                                      <div className="hotel-images">
                                        {dayData.hotels?.[0]?.hotelId?.seasonalRates?.[0]?.rates?.[0]?.images?.map((image, idx) => (
                                          <img
                                            key={idx}
                                            src={image}
                                            alt={`Hotel ${idx + 1}`}
                                            className="hotel-thumb"
                                            onClick={() => openImageModal(image)}
                                          />
                                        ))}
                                      </div>
                                      <button className="nav-btn next" onClick={() => handleSwipe(dayIdx, "right")}>
                                        <FontAwesomeIcon icon={faChevronRight} />
                                      </button>
                                    </div>
                                    {selectedImage && (
                                      <div className="image-modal" onClick={closeImageModal}>
                                        <div className="modal-content-img" onClick={e => e.stopPropagation()}>
                                          <button className="close-modal-btn" onClick={closeImageModal}>
                                            <FontAwesomeIcon icon={faTimes} />
                                          </button>
                                          <img src={selectedImage} alt="Zoomed view" />
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                </div>

                                {/* Cab Section */}
                                <div className="sub-section">
                                  <div className="sub-section-header">
                                    <h3 className="sub-section-title">Cab</h3>
                                    <button className="change-btn" onClick={() => handleViewCabClick(dayIdx)}>Change</button>
                                  </div>
                                  <ReadMoreText text={dayData.cab} limit={150} className="common-text" />
                                </div>

                                {/* Activities Section */}
                                {expandedDays[dayIdx] && (
                                  <div className="sub-section">
                                    <div className="sub-section-header">
                                      <h3 className="sub-section-title">Activities</h3>
                                      <button className="change-btn" onClick={() => handleViewActivitiesClick(dayIdx)}>Change</button>
                                    </div>
                                    {dayData.activities?.length > 0 ? (
                                      <ul className="itinerary-list">
                                        {dayData.activities.map((activity, idx) => (
                                          <li key={idx} className="itinerary-item">
                                            <div className="activity-content">
                                              <strong className="activity-name">{activity.name}:</strong>
                                              <ReadMoreText text={activity.description} limit={200} className="activity-desc" />
                                            </div>
                                          </li>
                                        ))}
                                      </ul>
                                    ) : (
                                      <p className="common-text">No activities planned for this day.</p>
                                    )}
                                  </div>
                                )}
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </main>
        </div>
      </div>
      <Footer />
      <FloatingAIButton onClick={openModal} />
      {isModalOpen && <Modal closeModal={closeModal} />}
      {isFormModalOpen && (
        <div style={computedStyles.modalOverlay}>
          <div style={computedStyles.modalContent}>
            <div style={computedStyles.modalHeader}>
              <h2 style={computedStyles.modalTitle}>Book Package</h2>
              <button
                style={computedStyles.breakdownButton}
                onClick={() => setIsBreakdownModalOpen(true)}
              >
                Package Breakdown
              </button>
            </div>
            {['name', 'email', 'phone', 'selectedPackage'].map((field, idx) => (
              <div key={idx}>
                <label style={computedStyles.inputLabel}>
                  {field.charAt(0).toUpperCase() + field.slice(1)}:
                  <input
                    type={field === 'email' ? 'email' : 'text'}
                    name={field}
                    value={formData[field]}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    disabled={field === 'selectedPackage'}
                    style={{
                      ...computedStyles.inputField,
                      backgroundColor: field === 'selectedPackage' ? '#f8f9fa' : 'white'
                    }}
                  />
                </label>
                {field === 'email' && errors.email && (
                  <div style={computedStyles.errorText}>{errors.email}</div>
                )}
                {field === 'phone' && errors.phone && (
                  <div style={computedStyles.errorText}>{errors.phone}</div>
                )}
              </div>
            ))}
            <div style={computedStyles.buttonContainer1}>
              <button
                onClick={handleSubmit}
                disabled={loadingSubmit || !formData.email.trim() || !formData.phone.trim()}
                style={{
                  ...computedStyles.submitButton,
                  backgroundColor:
                    loadingSubmit || !formData.email.trim() || !formData.phone.trim()
                      ? '#6c757d'
                      : '#28A745',
                  cursor:
                    loadingSubmit || !formData.email.trim() || !formData.phone.trim()
                      ? 'not-allowed'
                      : 'pointer'
                }}
              >
                {loadingSubmit ? 'Loading...' : 'Submit'}
              </button>
              <button onClick={formCloseModal} style={computedStyles.closeButton}>
                Close
              </button>
            </div>
            <PolicyModals />
          </div>
        </div>
      )}
      {isBreakdownModalOpen && (
        <div style={computedStyles.modalOverlay}>
          <div style={computedStyles.modalContent}>
            <h2 style={computedStyles.modalTitle}>Price Breakdown</h2>
            {(() => {
              const { dayBreakdowns, overall } = computeBreakdown();
              const originalTotal = overall.finalTotal;

              // Use the same formula as applyPromoCode for discount calculation
              // Discount is applied to Service Charge, then GST is calculated on net service charge
              let discountedFinalTotal = originalTotal;
              let discountAmt = 0;
              let netServiceCharge = overall.serviceCharge;
              let gstAfterDiscount = overall.gst;

              if (discountPercentage > 0) {
                discountAmt = overall.serviceCharge * (discountPercentage / 100);  // Discount on service charge
                netServiceCharge = overall.serviceCharge - discountAmt;  // Service charge after discount
                gstAfterDiscount = netServiceCharge * 0.18;  // 18% GST on net service charge
                discountedFinalTotal = overall.base + netServiceCharge + gstAfterDiscount;  // Net Payable Amount
              }

              return (
                <div>
                  {dayBreakdowns.map(day => (
                    <div key={day.day}>
                      <p style={{ fontFamily: "Merriweather, serif", fontSize: windowWidth < 480 ? "14px" : "16px" }}><strong>Day {day.day}:</strong></p>
                      <p style={{ fontFamily: "Merriweather, serif", fontSize: windowWidth < 480 ? "14px" : "16px" }}>Hotels: Rs {day.hotelsTotal.toFixed(2)}</p>
                      <p style={{ fontFamily: "Merriweather, serif", fontSize: windowWidth < 480 ? "14px" : "16px" }}>Cabs: Rs {day.cabsTotal.toFixed(2)}</p>
                      <p style={{ fontFamily: "Merriweather, serif", fontSize: windowWidth < 480 ? "14px" : "16px" }}>Activities: Rs {day.activitiesTotal.toFixed(2)}</p>
                      <p style={{ fontFamily: "Merriweather, serif", fontSize: windowWidth < 480 ? "14px" : "16px" }}>Day Total: Rs {day.dayTotal.toFixed(2)}</p>
                      <hr />
                    </div>
                  ))}
                  <h3 style={{ marginTop: "20px", color: "#333", fontFamily: "Merriweather, serif", fontSize: windowWidth < 480 ? "18px" : "20px" }}>Pricing Summary</h3>
                  <p style={{ fontFamily: "Merriweather, serif", fontSize: windowWidth < 480 ? "14px" : "16px" }}><strong>Cost Price: Rs {overall.base.toFixed(2)}</strong></p>
                  <p style={{ fontFamily: "Merriweather, serif", fontSize: windowWidth < 480 ? "14px" : "16px" }}>
                    Service Charge: Rs {discountPercentage > 0 ? (
                      <><span style={{ textDecoration: "line-through", color: "#888" }}>{overall.serviceCharge.toFixed(2)}</span> <span style={{ color: "#28a745" }}>{netServiceCharge.toFixed(2)}</span></>
                    ) : overall.serviceCharge.toFixed(2)}
                  </p>
                  {discountPercentage > 0 && (
                    <p style={{ fontFamily: "Merriweather, serif", fontSize: windowWidth < 480 ? "14px" : "16px", color: "#28a745" }}>
                      Discount ({discountPercentage}% on Service Charge): - Rs {discountAmt.toFixed(2)}
                    </p>
                  )}
                  <p style={{ fontFamily: "Merriweather, serif", fontSize: windowWidth < 480 ? "14px" : "16px" }}>
                    GST: Rs {gstAfterDiscount.toFixed(2)}
                  </p>
                  {discountPercentage === 0 ? (
                    <div style={{ marginTop: "20px", padding: "10px", backgroundColor: "#f4f4f4", borderRadius: "5px" }}>
                      <h3 style={{ color: "#333", marginBottom: "10px", fontFamily: "Merriweather, serif", fontSize: windowWidth < 480 ? "16px" : "18px" }}>Total payable amount</h3>
                      <p style={{ fontSize: windowWidth < 480 ? "16px" : "18px", fontWeight: "bold", color: "#28a745", fontFamily: "Merriweather, serif" }}>Final Price: Rs {originalTotal.toFixed(2)}</p>
                    </div>
                  ) : (
                    <div style={{ marginTop: "20px", padding: "10px", backgroundColor: "#f4f4f4", borderRadius: "5px" }}>
                      <h3 style={{ color: "#333", marginBottom: "10px", fontFamily: "Merriweather, serif", fontSize: windowWidth < 480 ? "16px" : "18px" }}>Total payable amount</h3>
                      <p>
                        <strong style={{ fontFamily: "Merriweather, serif", fontSize: windowWidth < 480 ? "14px" : "16px" }}>Original Total:</strong>
                        <span style={{ fontFamily: "Merriweather, serif", textDecoration: "line-through", color: "#888", marginLeft: "10px", fontSize: windowWidth < 480 ? "14px" : "16px" }}>Rs {originalTotal.toFixed(2)}</span>
                      </p>
                      <p style={{ color: "green", marginTop: "10px", fontFamily: "Merriweather, serif", fontSize: windowWidth < 480 ? "14px" : "16px" }}>{discountPercentage}% discount applied on service charge!</p>
                      <p style={{ fontSize: windowWidth < 480 ? "16px" : "18px", fontWeight: "bold", color: "#28a745", fontFamily: "Merriweather, serif" }}>Final Price after discount: Rs {discountedFinalTotal.toFixed(2)}</p>
                    </div>
                  )}
                </div>
              );
            })()}
            <button onClick={() => setIsBreakdownModalOpen(false)} style={{ ...computedStyles.closeButton, marginTop: "20px", width: "100%" }}>Close</button>
          </div>
        </div>
      )}
    </>
  );
}

const styles = {
  container: { display: "flex", fontFamily: "Merriweather, serif" },
  destinationPage: { fontFamily: "Merriweather, serif" },
  destinationHeader: { display: "flex", justifyContent: "space-between", alignItems: "center", position: "relative" },
  leftContent: { position: "absolute", top: "50%", transform: "translateY(-50%)", zIndex: 2, color: "white" },
  h1: { fontFamily: '"Merriweather, serif', fontWeight: 400, marginTop: "-30px", color: "#004e61" },
  breadcrumb: { fontSize: "27px", fontWeight: 300, fontFamily: 'Merriweather, serif', color: "#1ca8cb", marginTop: "5%" },
  breadcrumbLink: { textDecoration: "none", color: "#1ca8cb" },
  rightImage: { objectFit: "cover", filter: "brightness(50%)", position: "relative" },
  cityPhotoContainer: { position: "relative", width: "fit-content" },
  cityPhoto: { objectFit: "cover", filter: "brightness(80%)", position: "relative" },
  buttonsContainer: { position: "absolute", display: "flex", gap: "20px" },
  smallButton: { fontWeight: "700", fontFamily: "Merriweather, serif", backgroundColor: "#1CA8CB", color: "white", border: "none", borderRadius: "26px", cursor: "pointer" },
  sidebar: { padding: "10px" },
  horizontalLine: { border: "none", borderTop: "1px solid #ccc", margin: "10px 0" },
  mainContent: { padding: "0 20px" },
  tabs: { display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid #ddd" },
  tabButton: { flex: 1, border: "none", borderBottom: "2px solid transparent", background: "none", fontWeight: "700", cursor: "pointer", color: "#1CA8CB", fontFamily: "Merriweather, serif" },
  daysBadge: { backgroundColor: "#1CA8CB", padding: "10px 15px", borderRadius: "5px", color: "white", display: "flex", flexDirection: "column", alignItems: "center" },
  daysText: { fontSize: "24px", fontWeight: "bold" },
  daysLabel: { fontSize: "12px" },
  section: { marginTop: "30px" },
  mainHeading: { fontWeight: "700", marginBottom: "10px", color: "#004E61", fontFamily: "Merriweather, serif", lineHeight: "50px" },
  infoRow: { display: "flex", justifyContent: "space-between", marginBottom: "10px" },
  infoItem: { display: "flex", alignItems: "center", color: "#535353", fontWeight: "400", fontFamily: "Merriweather, serif", lineHeight: "28px", marginRight: "20px" },
  withBorder: { borderRight: "2px solid #1CA8CB", paddingRight: "20px", marginRight: "20px" },
  icon: { marginRight: "10px" },
  divider: { margin: "20px 0", border: "none", borderTop: "1px solid #ddd" },
  sectionHeading: { fontFamily: "Merriweather, serif", fontWeight: "700", marginBottom: "10px", color: "#004E61", lineHeight: "50px" },
  sectionText: { fontFamily: "Merriweather, serif", color: "#4A4A4A", fontWeight: "300", lineHeight: "28px" },
  sectionImage: { width: "100%", height: "auto" },
  contactCard: { position: "relative", height: "auto", backgroundImage: "url('https://images.unsplash.com/photo-1544735716-87fa59a45b4e?w=600&auto=format&fit=crop&q=60&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxzZWFyY2h8Nnx8bW91bnQlMjBldmVyZXN0fGVufDB8fDB8fHww')", backgroundSize: "cover", backgroundPosition: "center", borderRadius: "1px", overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center" },
  overlay: { position: "absolute", top: 0, left: 0, width: "100%", height: "100%", backgroundColor: "#03576C", opacity: 0.77 },
  contactContent: { position: "relative", color: "white", zIndex: 1, padding: "20px", textAlign: "center" },
  cardHeading: { fontWeight: "700", marginBottom: "10px", textAlign: "left", fontFamily: "Merriweather, serif" },
  cardText: { fontFamily: "Merriweather, serif", fontWeight: "300", marginBottom: "20px", lineHeight: "28px", textAlign: "left" },
  contactInfo: { display: "flex", flexDirection: "column", alignItems: "left", gap: "10px" },
  contactDetail: { display: "flex", justifyContent: "flex-start", gap: "10px", fontSize: "16px", fontFamily: "Merriweather, serif", fontWeight: "700", lineHeight: "28px" },
  carouselContainer: { position: "relative" },
  carouselImage: { width: "100%" },
  nextArrow: { position: "absolute", top: "50%", right: "10px", transform: "translateY(-50%)", cursor: "pointer", fontSize: "24px", zIndex: 1 },
  prevArrow: { position: "absolute", top: "50%", left: "10px", transform: "translateY(-50%)", cursor: "pointer", fontSize: "24px", zIndex: 1 },
  subHeading: { fontFamily: "Merriweather, serif", fontSize: "20px", fontWeight: "400", color: "#1CA8CB", marginTop: "20px", marginBottom: "10px" },
  costText: { fontFamily: "Merriweather, serif", fontSize: "17px", fontWeight: "300", color: "#4A4A4A", lineHeight: "28px", textAlign: "justify" },
  list: { listStyleType: "none", paddingLeft: "0" },
  iconCheck: { color: "#008415", marginRight: "10px" },
  iconCross: { color: "#D60000", marginRight: "10px" },
  itineraryContent: { marginTop: "10px" },
  timelineWrapper: { position: "relative", marginLeft: "30px" },
  verticalLine: { position: "absolute", top: 0, left: "-20px", width: "2px", height: "100%", backgroundColor: "#1CA8CB", zIndex: -1 },
  dayHeading: { fontFamily: "Merriweather, serif", fontSize: "18px", fontWeight: "400", color: "#02495B", lineHeight: "1.4", textAlign: "left" },
  infoContainer: { display: "flex", justifyContent: "space-between", marginTop: "15px", marginBottom: "20px" },
  hotelSection: { marginBottom: "20px" },
  cabSection: { marginBottom: "20px" },
  changeButton: { backgroundColor: "#02495B", color: "white", border: "none", padding: "5px 30px", borderRadius: "30px", cursor: "pointer", fontFamily: "Merriweather, serif", fontSize: "12px", fontWeight: "300", lineHeight: "28px", marginLeft: "50px" },
  hotelContainer: { marginTop: "10px", overflow: "hidden" },
  swipeableContainer: { display: "flex", transition: "transform 0.5s ease", justifyContent: "center" },
  hotelImage: { width: "201px", height: "133px", margin: "0 5px" },
  userAvatar: { width: "66px", height: "66px", borderRadius: "50%", backgroundColor: "#D9D9D9", marginRight: "10px" },
  userDetails: { flex: 1 },
  userName: { fontFamily: "Merriweather, serif", fontSize: "23px", fontWeight: "700", lineHeight: "35px", textAlign: "justify", color: "#1CA8CB" },
  userStars: { display: "flex", marginTop: "5px" },
  grayStar: { color: "#D9D9D9", marginRight: "3px" },
  userComment: { fontFamily: "Merriweather, serif", fontSize: "13px", fontWeight: "300", lineHeight: "30px", textAlign: "justify", marginTop: "5px", color: "#4A4A4A" },
  formSection: { marginTop: "20px" },
  formHeading: { fontFamily: "Merriweather, serif", fontSize: "23px", fontWeight: "700", lineHeight: "35px", textAlign: "justify", color: "#1CA8CB" },
  formGroup: { marginBottom: "15px" },
  formControl: { width: "100%", padding: "10px", borderRadius: "5px", border: "1px solid #1CA8CB" },
  floatingBox: { position: "fixed", bottom: "20px", right: "115px", width: "96px", height: "96px", display: "flex", borderRadius: "15px", justifyContent: "center", alignItems: "center", zIndex: 10 },
  floatingButton: { border: "none", backgroundColor: "transparent", padding: 0, cursor: "pointer", borderRadius: "15px" },
  floatingImage: { width: "96px", height: "96px", objectFit: "cover", borderRadius: "15px" },
  bookButton: { padding: '10px 30px', border: 'none', borderRadius: '10px', cursor: 'pointer', fontFamily: 'Merriweather, serif', fontSize: '22px', fontWeight: 700, color: 'white', backgroundColor: '#1CA8CB' },
  buttonContainer: { textAlign: 'right' },
  priceText: { color: '#02495B', fontSize: '28px', fontFamily: 'Merriweather, serif', fontWeight: 700, textAlign: 'right' },
  priceSubtext: { color: '#4A4A4A', fontSize: '16px', fontFamily: 'Merriweather, serif', fontWeight: 400, textAlign: 'right', marginTop: -20 },
  activitiesSection: { marginTop: "20px", padding: "10px", borderTop: "1px solid #e0e0e0" },
  modalOverlay: { position: "fixed", top: 0, left: 0, right: 0, bottom: 0, backgroundColor: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: "20px", overflowY: "auto" },
  modalContent: { backgroundColor: "#fff", padding: "20px", borderRadius: "8px", width: "100%", maxWidth: "600px", boxShadow: "0 4px 10px rgba(0,0,0,0.25)", maxHeight: "90vh", overflowY: "auto" },
  modalHeader: { fontFamily: "Merriweather, serif", display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "15px" },
  modalTitle: { fontFamily: "Merriweather, serif", fontSize: "24px", fontWeight: "700", color: "#004E61", margin: 0 },
  breakdownButton: { fontFamily: "Merriweather, serif", backgroundColor: "#1CA8CB", color: "#fff", padding: "8px 12px", border: "none", borderRadius: "4px", cursor: "pointer", fontSize: "14px" },
  inputLabel: { fontFamily: "Merriweather, serif", display: "block", marginBottom: "10px", fontSize: "16px", color: "#004E61" },
  inputField: { fontFamily: "Merriweather, serif", width: "100%", padding: "10px", margin: "5px 0 15px", border: "1px solid #ccc", borderRadius: "4px" },
  errorText: { fontFamily: "Merriweather, serif", color: 'red', fontSize: '0.8rem', marginTop: '4px' },
  buttonContainer1: { display: "flex", justifyContent: "space-between", marginTop: "20px" },
  submitButton: { padding: "10px 20px", border: "none", borderRadius: "4px", color: "#fff", fontSize: "16px", cursor: "pointer" },
  closeButton: { padding: "10px 20px", border: "none", borderRadius: "4px", backgroundColor: "#ccc", color: "#333", fontSize: "16px", cursor: "pointer" },
  promoButtonContainer: { marginTop: "20px", textAlign: "center", },
  promoToggleButton: { padding: "10px 20px", backgroundColor: "#1CA8CB", color: "white", border: "none", borderRadius: "5px", cursor: "pointer", fontSize: "16px", fontFamily: "Merriweather, serif", width: "100%", maxWidth: "300px", },
  promoContainer: { marginTop: "20px", padding: "15px", backgroundColor: "#f8f9fa", borderRadius: "8px", maxHeight: "400px", overflowY: "auto", },
  promoCard: { backgroundColor: "white", border: "1px solid #e0e0e0", borderRadius: "8px", padding: "15px", marginBottom: "15px", boxShadow: "0 2px 5px rgba(0,0,0,0.1)", },
  promoCardTitle: { fontSize: "18px", fontWeight: "bold", color: "#004E61", marginBottom: "10px", fontFamily: "Merriweather, serif", },
  promoCardText: { fontSize: "14px", color: "#4A4A4A", margin: "5px 0", fontFamily: "Merriweather, serif", },
  promoApplyButton: { padding: "8px 15px", backgroundColor: "#28A745", color: "white", border: "none", borderRadius: "5px", cursor: "pointer", fontSize: "14px", marginTop: "10px", fontFamily: "Merriweather, serif", width: "100%", opacity: (props) => (props.disabled ? 0.6 : 1), pointerEvents: (props) => (props.disabled ? "none" : "auto"), },
};
export default TripDetails;